import { CircleAlert } from 'lucide-react'
import { type ComponentProps, createContext, type ReactNode, use, useId, useMemo } from 'react'

import { cn } from '../lib/cn'

interface FormFieldContextValue {
  id: string
  labelId: string
  hintId: string | undefined
  errorId: string | undefined
  invalid: boolean
  required: boolean
  disabled: boolean
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null)

/** Contexto do FormField mais próximo (ou null fora de um). */
export function useFormField(): FormFieldContextValue | null {
  return use(FormFieldContext)
}

interface FieldControlInput {
  id?: string
  'aria-describedby'?: string
  invalid?: boolean
  required?: boolean
  disabled?: boolean
}

/**
 * Atributos de acessibilidade que um controle recebe do FormField: id (para o rótulo),
 * aria-describedby (ajuda e erro), aria-invalid e aria-required. Props explícitas vencem.
 * `required` vira só aria-required, para o navegador não mostrar balões próprios em inglês.
 */
export function useFieldControl(props: FieldControlInput) {
  const field = use(FormFieldContext)
  const invalid = props.invalid ?? field?.invalid ?? false
  const required = props.required ?? field?.required ?? false
  const describedBy =
    [props['aria-describedby'], field?.hintId, field?.errorId].filter(Boolean).join(' ') ||
    undefined
  return {
    id: props.id ?? field?.id,
    disabled: props.disabled ?? field?.disabled,
    invalid,
    ariaProps: {
      'aria-describedby': describedBy,
      'aria-invalid': invalid || undefined,
      'aria-required': required || undefined,
    },
  }
}

export function Label({ className, ...props }: ComponentProps<'label'>) {
  return (
    // O htmlFor chega pelas props; quem usa Label sempre associa a um controle.
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label className={cn('text-compact font-medium text-ink select-none', className)} {...props} />
  )
}

export interface FormFieldProps {
  /** Rótulo visível (sentence case). */
  label: ReactNode
  /** Ajuda curta abaixo do campo. Some quando há erro. */
  hint?: ReactNode
  /** Mensagem de erro: explica e orienta. Presente = campo inválido. */
  error?: ReactNode
  required?: boolean
  disabled?: boolean
  /** Id do controle; gerado quando omitido. */
  id?: string
  className?: string
  /** O controle: Input, PasswordInput, Textarea, SelectTrigger, Combobox, DatePicker, RadioGroup. */
  children: ReactNode
}

/** Rótulo + controle + ajuda + erro, com as associações de acessibilidade feitas. */
export function FormField({
  label,
  hint,
  error,
  required = false,
  disabled = false,
  id,
  className,
  children,
}: FormFieldProps) {
  const autoId = useId()
  const fieldId = id ?? `field${autoId.replace(/:/g, '')}`
  const hasError = error != null && error !== '' && error !== false
  const hasHint = !hasError && hint != null && hint !== '' && hint !== false

  const context = useMemo<FormFieldContextValue>(
    () => ({
      id: fieldId,
      labelId: `${fieldId}-label`,
      hintId: hasHint ? `${fieldId}-hint` : undefined,
      errorId: hasError ? `${fieldId}-error` : undefined,
      invalid: hasError,
      required,
      disabled,
    }),
    [fieldId, hasHint, hasError, required, disabled],
  )

  return (
    <FormFieldContext value={context}>
      <div
        className={cn('flex flex-col gap-1.5', className)}
        data-invalid={hasError || undefined}
        data-disabled={disabled || undefined}
      >
        <Label id={context.labelId} htmlFor={fieldId}>
          {label}
        </Label>
        {children}
        {hasError ? (
          <p id={context.errorId} className="flex items-start gap-1.5 text-compact text-framboesa">
            <CircleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
            <span>{error}</span>
          </p>
        ) : null}
        {hasHint ? (
          <p id={context.hintId} className="text-compact text-ink-2">
            {hint}
          </p>
        ) : null}
      </div>
    </FormFieldContext>
  )
}
